import { useQuery } from '@tanstack/react-query'
import { baseClient } from 'builder/modules/apiClient'
import { DailySuggestionsResponse } from 'builder/modules/interview/types'
import { DAILY_REGULAR_POLL_INTERVAL, POLLING_REFRESH_INTERVAL_IN_MILLIS } from '../constants'
import { InitialRequest } from '../view/DashboardView/DashboardView'
import { usePerformanceOnce } from './usePerformanceOnce'

export const useDailySuggestions = (
  jobTitleId: number | null,
  setInitialRequest?: React.Dispatch<React.SetStateAction<InitialRequest | undefined>>,
  enabledPerformanceMetric = false,
) => {
  const { registerOncePerformance, stopRegisterPerformance } = usePerformanceOnce({
    enabled: enabledPerformanceMetric,
    metricKey: `daily-suggestion-request`,
    tags: { jobTitleId },
    successCallBack: () => {
      if (setInitialRequest) {
        setInitialRequest(prevState =>
          prevState === undefined ? undefined : { ...prevState, dailyRequest: true },
        )
      }
    },
  })

  return useQuery(
    ['useDailySuggestions', jobTitleId],
    async () => {
      registerOncePerformance()
      const response = await baseClient.get<DailySuggestionsResponse>(
        `/interview_prep/daily_suggestions?data_api_job_title_id=${jobTitleId}`,
      )
      if (response.data.error) {
        return null
      }

      return {
        question_templates: response.data.question_templates.map(question => ({
          ...question,
          kind: 'one_question',
        })),
        interview_template: response.data.interview_template
          ? {
              ...response.data.interview_template,
              kind: 'one_question',
            }
          : undefined,
        company_interview: response.data.company_interview
          ? {
              ...response.data.company_interview,
              kind: 'custom',
            }
          : undefined,
      }
    },
    {
      enabled: typeof jobTitleId === 'number',
      refetchInterval: (data?: DailySuggestionsResponse) => {
        if (!(data as unknown as DailySuggestionsResponse)?.error) {
          stopRegisterPerformance()
          return DAILY_REGULAR_POLL_INTERVAL
        }
        return POLLING_REFRESH_INTERVAL_IN_MILLIS
      },
    },
  )
}
